import React from 'react';
import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';
import { Clock, CheckCircle2, BookOpen, Compass, HelpCircle } from 'lucide-react';

export const GettingStartedPage: React.FC = () => {
  return (
    <DocsLayout
      title='Getting Started'
      description='Learn how to log in and start using Vehix'
      breadcrumbs={[{ label: 'Documentation', path: '/docs' }, { label: 'Getting Started' }]}
    >
      <div className='max-w-[1000px]'>
        {/* Quick Start Card */}
        <section className='mb-20'>
          <div className='border rounded-lg p-6 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800'>
            <div className='flex items-center gap-3 mb-4'>
              <Clock className='h-8 w-8 text-blue-600 dark:text-blue-400' />
              <div>
                <h2 className='text-lg font-bold text-blue-900 dark:text-blue-100 mb-1'>
                  Quick Start
                </h2>
                <p className='text-sm text-blue-700 dark:text-blue-300'>
                  Get started using Vehix right away
                </p>
              </div>
            </div>
            <div className='space-y-3 ml-11'>
              <div className='flex items-start gap-3'>
                <span className='px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs font-medium rounded border border-blue-300 dark:border-blue-700'>
                  Step 1
                </span>
                <span className='text-sm leading-6 text-blue-900 dark:text-blue-100'>
                  Get your login credentials from your manager
                </span>
              </div>
              <div className='flex items-start gap-3'>
                <span className='px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs font-medium rounded border border-blue-300 dark:border-blue-700'>
                  Step 2
                </span>
                <span className='text-sm leading-6 text-blue-900 dark:text-blue-100'>
                  Log in to the Vehix system
                </span>
              </div>
              <div className='flex items-start gap-3'>
                <span className='px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs font-medium rounded border border-blue-300 dark:border-blue-700'>
                  Step 3
                </span>
                <span className='text-sm leading-6 text-blue-900 dark:text-blue-100'>
                  Set your password (first time only)
                </span>
              </div>
              <div className='flex items-start gap-3'>
                <span className='px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs font-medium rounded border border-blue-300 dark:border-blue-700'>
                  Step 4
                </span>
                <span className='text-sm leading-6 text-blue-900 dark:text-blue-100'>
                  Start tracking and completing recalls
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Getting Your Account */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Getting Your Account</h2>

          <div className='space-y-6'>
            <div>
              <h3 className='text-lg font-semibold mb-3'>1. Account Setup by Your Manager</h3>
              <p className='text-sm leading-6'>
                Your service manager or administrator will create your Vehix account using your
                company email address. They'll provide you with your login credentials (company
                email and temporary password).
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-3'>2. How to Log In</h3>
              <p className='text-sm leading-6 mb-3'>
                Vehix uses secure email and password authentication:
              </p>
              <ul className='space-y-2 text-sm leading-6 list-disc pl-5'>
                <li>
                  <strong>Company Email Required:</strong> You must use your official
                  company/dealership email address
                </li>
                <li>
                  <strong>Password:</strong> Your manager will provide a temporary password that
                  you'll change on first login
                </li>
                <li>
                  <strong>Security:</strong> Your account is managed by your dealership
                  administrator for security
                </li>
              </ul>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-3'>3. What You'll Need</h3>
              <p className='text-sm leading-6 mb-4'>Before your first login, make sure you have:</p>

              <div className='border rounded-lg p-6 bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800'>
                <h4 className='text-lg font-bold text-yellow-900 dark:text-yellow-100 mb-4'>
                  Required Information
                </h4>
                <ul className='space-y-3'>
                  <li className='flex items-start gap-3'>
                    <CheckCircle2 className='h-5 w-5 text-yellow-600 dark:text-yellow-500 mt-0.5 flex-shrink-0' />
                    <span className='text-sm leading-6 text-yellow-900 dark:text-yellow-100'>
                      Your company email and temporary password from your manager
                    </span>
                  </li>
                  <li className='flex items-start gap-3'>
                    <CheckCircle2 className='h-5 w-5 text-yellow-600 dark:text-yellow-500 mt-0.5 flex-shrink-0' />
                    <span className='text-sm leading-6 text-yellow-900 dark:text-yellow-100'>
                      The Vehix website address (portal.vehix.ai)
                    </span>
                  </li>
                  <li className='flex items-start gap-3'>
                    <CheckCircle2 className='h-5 w-5 text-yellow-600 dark:text-yellow-500 mt-0.5 flex-shrink-0' />
                    <span className='text-sm leading-6 text-yellow-900 dark:text-yellow-100'>
                      A computer or tablet with internet access
                    </span>
                  </li>
                  <li className='flex items-start gap-3'>
                    <CheckCircle2 className='h-5 w-5 text-yellow-600 dark:text-yellow-500 mt-0.5 flex-shrink-0' />
                    <span className='text-sm leading-6 text-yellow-900 dark:text-yellow-100'>
                      A modern web browser (Chrome is preferred, but Firefox, Safari, or Edge also
                      work)
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Your First Login */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Your First Login</h2>

          <div className='space-y-6'>
            <div>
              <h3 className='text-lg font-semibold mb-3'>1. Access the Login Page</h3>
              <p className='text-sm leading-6 mb-3'>
                Open your web browser and go to <strong>portal.vehix.ai</strong>. You'll see the
                Vehix login screen with fields for your email and password.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-3'>2. Enter Your Credentials</h3>
              <ol className='space-y-2 text-sm leading-6 list-decimal pl-5'>
                <li>Enter your email address and the temporary password from your manager</li>
                <li>
                  Click the <strong>"Sign In"</strong> button
                </li>
                <li>If this is your first login, you'll be prompted to create a new password</li>
                <li>Choose a strong password that you'll remember</li>
                <li>
                  If you forget your password later, use the "Forgot my password" link on the login
                  screen
                </li>
                <li>You'll be taken to the Vehix dashboard</li>
              </ol>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-3'>3. Welcome to Vehix</h3>
              <p className='text-sm leading-6 mb-3'>
                After logging in, you'll see the <strong>Vehix Dashboard</strong>. This is your home
                base where you'll:
              </p>
              <ul className='space-y-2 text-sm leading-6 list-disc pl-5'>
                <li>
                  <strong>View today's recalls</strong> that need attention
                </li>
                <li>
                  <strong>Access the recall map</strong> to see vehicle locations
                </li>
                <li>
                  <strong>Track your progress</strong> on assigned routes
                </li>
                <li>
                  <strong>Find specific recalls</strong> using search tools
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Understanding Your Role */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Understanding Your Role</h2>
          <p className='text-sm leading-6 mb-6'>
            Your role in Vehix determines what features you can access. Your manager will assign you
            one of these roles based on your job responsibilities:
          </p>

          <div className='grid gap-4 md:grid-cols-2'>
            <div className='border rounded-lg p-5'>
              <h3 className='text-lg font-bold mb-4'>User Roles</h3>
              <ul className='space-y-2.5 text-sm leading-6'>
                <li>
                  <strong>Technician</strong> - Executes recall services
                </li>
                <li>
                  <strong>Lead Technician</strong> - Senior technician with oversight
                </li>
                <li>
                  <strong>Service Advisor</strong> - Customer-facing operations
                </li>
                <li>
                  <strong>Service Manager</strong> - Departmental management
                </li>
                <li>
                  <strong>Viewer</strong> - Read-only access
                </li>
              </ul>
            </div>

            <div className='border rounded-lg p-5'>
              <h3 className='text-lg font-bold mb-4'>Admin Roles</h3>
              <ul className='space-y-2.5 text-sm leading-6'>
                <li>
                  <strong>Field Operations Director</strong> - Multi-location oversight
                </li>
                <li>
                  <strong>Dealership Admin</strong> - Dealership-wide management
                </li>
                <li>
                  <strong>Organization Owner</strong> - Organization management
                </li>
                <li>
                  <strong>Super Admin</strong> - Full system access
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* What to Do First */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>What to Do First</h2>
          <p className='text-sm leading-6 mb-4'>
            Based on your role, here's what you should do after logging in:
          </p>

          <ul className='space-y-2 text-sm leading-6 list-disc pl-5'>
            <li>
              <strong>Technicians:</strong> Check your assigned routes and see which recalls you
              need to complete today
            </li>
            <li>
              <strong>Service Advisors:</strong> Look for new recalls that need to be assigned to
              technicians
            </li>
            <li>
              <strong>Service Managers:</strong> Review your team's progress and check for any
              high-priority recalls
            </li>
            <li>
              <strong>Administrators:</strong> Verify your dealer locations are set up correctly and
              review user permissions
            </li>
          </ul>
        </section>

        {/* Next Steps */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Next Steps</h2>
          <p className='text-sm leading-6 mb-6'>
            Now that you're logged in and familiar with the basics, you can:
          </p>

          <div className='space-y-4'>
            <Link to='/docs/features' className='block'>
              <div className='border rounded-lg p-5 hover:border-blue-500 dark:hover:border-blue-400 transition-colors'>
                <div className='flex items-start gap-3'>
                  <BookOpen className='h-8 w-8 text-blue-600 dark:text-blue-400 flex-shrink-0' />
                  <div>
                    <h3 className='text-lg font-semibold mb-1'>Explore Features</h3>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Learn about the Recall Map, Manual Routing, Route Tracker, and more
                    </p>
                  </div>
                </div>
              </div>
            </Link>

            <Link to='/docs/guides' className='block'>
              <div className='border rounded-lg p-5 hover:border-blue-500 dark:hover:border-blue-400 transition-colors'>
                <div className='flex items-start gap-3'>
                  <Compass className='h-8 w-8 text-blue-600 dark:text-blue-400 flex-shrink-0' />
                  <div>
                    <h3 className='text-lg font-semibold mb-1'>Browse How-To Guides</h3>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Step-by-step instructions for common workflows and daily tasks
                    </p>
                  </div>
                </div>
              </div>
            </Link>

            <Link to='/docs/support' className='block'>
              <div className='border rounded-lg p-5 hover:border-blue-500 dark:hover:border-blue-400 transition-colors'>
                <div className='flex items-start gap-3'>
                  <HelpCircle className='h-8 w-8 text-blue-600 dark:text-blue-400 flex-shrink-0' />
                  <div>
                    <h3 className='text-lg font-semibold mb-1'>Get Support</h3>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Learn how to get help when you need it
                    </p>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* Password Help */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Forgot Your Password?</h2>
          <p className='text-sm leading-6 mb-4'>
            Don't worry! You can reset your password yourself:
          </p>
          <ol className='space-y-2 text-sm leading-6 list-decimal pl-5'>
            <li>
              Go to <strong>portal.vehix.ai</strong>
            </li>
            <li>Click the "Forgot my password" link below the sign-in button</li>
            <li>Enter your company email address</li>
            <li>Check your email for reset instructions</li>
            <li>Follow the link to create a new password</li>
          </ol>
        </section>

        {/* Need Help */}
        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Need Help?</h2>
          <p className='text-sm leading-6'>
            If you have questions or need assistance, ask your service manager or visit our{' '}
            <Link to='/docs/support' className='text-blue-600 dark:text-blue-400 hover:underline'>
              Support page
            </Link>{' '}
            for help options.
          </p>
        </section>
      </div>
    </DocsLayout>
  );
};
